import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router';
import sessionStore from 'client/Session/SessionStore';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import {addToast, Anchor, removeToast} from '@albert-io/atomic';
import {history} from 'client/history';
import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import useLocalStorageTimer from 'lib/hooks/useLocalStorageTimer';
import PromptGradeLevelModal from 'client/Classrooms/Classroom/PromptGradeLevelModal/PromptGradeLevelModal.react';

import classroomStore from './ClassroomV2.store';

const Classroom = ({children, params}) => {
  const settingsLink = (
    <Anchor underlined inheritColor as={Link} to={`/classes/${params.classId}/settings/details`}>
      Settings
    </Anchor>
  );
  useEffect(() => {
    handleMasqueradeRedirect();
    const classroom = classroomStore.getActiveClassroom();
    if (classroom.getStatus() === 'archived') {
      const toastMessage = <p>You can un-archive this class via the class {settingsLink}</p>;
      const toast = {
        type: 'success',
        title: 'This class has been archived',
        message: toastMessage,
        permanent: true,
        toastId: params.classId
      };
      addToast(toast);
    }

    return () => {
      if (classroom.getStatus() === 'archived') {
        removeToast(params.classId);
      }
    };
  }, []);

  const isStudent = sessionStore.isStudent();
  const classroom = classroomStore.getActiveClassroom();
  const hasGrades = !classroom.getGradeLevels().isEmpty();

  const skipTimer = isStudent || hasGrades || masqueradeStore.isMasquerading();

  const localStorageKey = `classroom-${classroom.getName()}-${params.classId}`;

  const {hasExpired, resetTimer, clearTimer} = useLocalStorageTimer({
    skipTimer,
    timeLimitHours: 8,
    storageKey: localStorageKey
  });

  const handleMasqueradeRedirect = () => {
    const students = classroomStore.getClassroomStudents();
    const teacher = classroomStore.getClassroomTeacher();
    if (
      students.find((s) => s.getId() === sessionStore.getUserId()) === false &&
      teacher !== sessionStore.getUserId() &&
      masqueradeStore.isMasquerading() === false &&
      teacher !== masqueradeStore.getUserIdByMasqueradeState()
    ) {
      history.pushState(null, '/classes');
    }
  };
  // This relies on the store updating the classroom after invalidation for skipTimer (hasGrades)
  const showModal = hasExpired && !skipTimer;
  return (
    <>
      {showModal && (
        <PromptGradeLevelModal
          classroom={classroomStore.getActiveClassroom()}
          handleResetTimer={resetTimer}
          handleClearTimer={clearTimer}
        />
      )}
      {children &&
        React.cloneElement(children, {
          classroom: classroomStore.getActiveClassroom()
        })}
    </>
  );
};

Classroom.propTypes = {
  children: PropTypes.node,
  params: PropTypes.shape({
    classId: PropTypes.string
  })
};

export default awaitMandarkQueries(
  () => ({
    queries: [classroomStore.getClassroomQuery()],
    onInvalidQuery: () => {
      history.pushState(null, '/classes');
    }
  }),
  Classroom
);
